import { useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'tongren');

  const currentCategory = useMemo(() => categories.find((c) => c.slug === activeSlug), [activeSlug]);
  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeSlug).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [activeSlug],
  );

  const handleTabChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`, { replace: true });
  };

  return (
    <>
      <SEOHead
        title={currentCategory ? `${currentCategory.name} - 分类浏览` : '分类浏览'}
        description={currentCategory?.description || '浏览无翼鸟漫画各分类文章，包括日系同人漫画、少年漫画、少女漫画、青年漫画、原创漫画及创作教程。'}
        keywords={`${currentCategory?.name || '漫画分类'},无翼鸟漫画,漫画资源,漫画阅读,漫画分类浏览`}
        canonicalUrl={`/category/${activeSlug}`}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${currentCategory?.name || '分类浏览'} | 无翼鸟漫画`,
          description: currentCategory?.description || '',
          url: `https://wingless-bird-manga.readdy.ai/category/${activeSlug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '无翼鸟漫画',
            url: 'https://wingless-bird-manga.readdy.ai',
          },
        }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <section className="pt-24 pb-8 md:pt-28 md:pb-10 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '分类浏览', to: '/category/tongren' },
                { label: currentCategory?.name || '' },
              ]}
            />
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mt-4 mb-2">
              {currentCategory?.name || '分类浏览'}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl">
              {currentCategory?.description}
            </p>
          </div>
        </section>

        <section className="py-6">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleTabChange(cat.slug)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Article Grid */}
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-500/20 transition-all duration-200"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{article.date}</span>
                      </div>
                      <h3 className="font-semibold text-foreground-950 mb-2 line-clamp-2 text-sm md:text-base group-hover:text-primary-500 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-background-200/70">
                        {article.tags.map((tag) => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-foreground-500 whitespace-nowrap">
                          <i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()}
                        </span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">
                          <i className="ri-user-line mr-1"></i>{article.author}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}